import {
  AppstoreOutlined,
  EyeInvisibleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import { useLocalizable } from '@contexts/LocalizableContext'
import {
  eventsCollectionIsDynamic,
  EventsCollection as EventsCollectionModel,
} from '@models/EventsCollection'
import { useAppSelector } from '@store/hooks'
import { selectEventsByIds } from '@store/membership/EventReducer'
import { selectEventsCollectionById } from '@store/membership/EventsCollectionReducer'
import { SBModelComponent } from '@supplement-bacon/react-toolkit/models'
import { SBRM, SBRMAction } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import { Flex, Space, Tag, Tooltip, Typography } from 'antd'
import dayjs from 'dayjs'
import { langList } from '../../factories/Utils'

const { Text } = Typography
type Props = SBModelComponent<EventsCollectionModel> & {}

const EventsCollectionCardHeader = ({ id, model }: Props) => {
  const localizable = useLocalizable()

  const langCode = localizable?.langCode ?? langList[0].code
  const item = model ?? useAppSelector(selectEventsCollectionById(id!))

  const events = useAppSelector(selectEventsByIds(item?.events || []))

  const formatPeriod = (periodStart?: number, periodEnd?: number): string => {
    const formatStart = (value?: number) =>
      value === 0 ? 'today' : `+${value}d`
    const formatEnd = (value?: number) => (!value ? '∞' : `+${value}d`)
    return `${formatStart(periodStart)} → ${formatEnd(periodEnd)}`
  }
  if (!item) {
    return <></>
  }
  const allEventsArePast = events.every((event) =>
    dayjs(event.end).isBefore(dayjs())
  )
  const allEventsAreUnpublished = events.every((event) => !event.isPublished)

  const isHidden =
    !item.events?.length || allEventsArePast || allEventsAreUnpublished

  return (
    <Flex justify="space-between" align="center">
      <Flex align="center" gap={8}>
        <Text>{item[langCode].title}</Text>
      </Flex>
      <Space>
        {isHidden && (
          <Tooltip title="This collection is currently hidden because all of its associated events are either past or unpublished.">
            <Tag color="orange" icon={<EyeInvisibleOutlined />}>
              Hidden
            </Tag>
          </Tooltip>
        )}
        {eventsCollectionIsDynamic(item) && (
          <Tag color="blue" icon={<ThunderboltOutlined />}>
            {`Dynamic | ${formatPeriod(item.periodStart, item.periodEnd)}`}
          </Tag>
        )}
        <Tag icon={<AppstoreOutlined />}>
          {item.direction === true ? 'Vertical' : 'Horizontal'}
        </Tag>
        <SBRM.Actions
          actions={[SBRMAction.Update, SBRMAction.Delete]}
          entity={Entity.eventsCollection}
          entityId={item.id}
        />
      </Space>
    </Flex>
  )
}

type EventsCollectionCardHeaderType = {
  Header: typeof EventsCollectionCardHeader
}

export { EventsCollectionCardHeader, type EventsCollectionCardHeaderType }
